import { LOCAL_STORAGE_KEYS } from "@config/constant";

/**
 * My goals · Pending 卡「滑走 skip」的本地记录 —— `Record<ownerId, Record<goalId, expiryTs>>`。
 *
 * 语义与 Plan Banner 的 snooze 完全一致（见 `planBannerSnoozedGoalStorage.ts`）：
 * **当日隐藏**，过期时间取本地时区今天结束，第二天自然重新出现。
 *
 * ⚠️ 为什么不复用 banner 的 snooze 表：My goals 的 Pending 段与 Activity 的 banner 是两个面
 * （`usePendingActivateGoals` 注释里的 AC-P3 明确「不应用 banner 的 snooze/dismiss」）。
 * 共用一张表会让「在 Activity 里划掉一条」连带把 My goals 的卡也吞掉，反之亦然。
 */
type SkippedGoalExpiryByOwner = Record<string, Record<string, number>>;

function readSkippedMap(): SkippedGoalExpiryByOwner {
  if (typeof window === "undefined") {
    return {};
  }

  const raw = localStorage.getItem(
    LOCAL_STORAGE_KEYS.PLAN_PENDING_GOAL_SKIPPED_IDS,
  );
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return parsed as SkippedGoalExpiryByOwner;
  } catch {
    return {};
  }
}

function writeSkippedMap(map: SkippedGoalExpiryByOwner): void {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.PLAN_PENDING_GOAL_SKIPPED_IDS,
    JSON.stringify(map),
  );
}

/** 本地时区今天结束（23:59:59.999）的 epoch ms（原生 Date，与 banner snooze 同实现） */
function getEndOfTodayMs(): number {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end.getTime();
}

/** 滑走一张 Pending 卡 —— 持久化 goal_id，当日不再出现（明天恢复）。 */
export function persistPendingGoalSkip(ownerId: string, goalId: string): void {
  const normalizedOwnerId = ownerId.trim();
  const normalizedGoalId = goalId.trim();
  if (!normalizedOwnerId || !normalizedGoalId) {
    return;
  }

  const map = readSkippedMap();
  const byOwner = map[normalizedOwnerId] ?? {};
  byOwner[normalizedGoalId] = getEndOfTodayMs();
  map[normalizedOwnerId] = byOwner;
  writeSkippedMap(map);
}

/**
 * 读取当前 owner 仍处于当日隐藏期（未过期）的 goal_id 集合 ——
 * 后端拉回列表后按它直接排除。读取时顺带清理过期/非法条目（懒清理），避免无限增长。
 */
export function readActivePendingGoalSkipIdSet(ownerId: string): Set<string> {
  const normalizedOwnerId = ownerId.trim();
  if (!normalizedOwnerId) {
    return new Set();
  }

  const map = readSkippedMap();
  const byOwner = map[normalizedOwnerId];
  if (!byOwner) {
    return new Set();
  }

  const now = Date.now();
  const active: Record<string, number> = {};
  const activeIds = new Set<string>();
  let hasStale = false;

  for (const [goalId, expiry] of Object.entries(byOwner)) {
    const trimmedId = goalId.trim();
    const expiryTs = typeof expiry === "number" ? expiry : Number(expiry);
    if (!trimmedId || !Number.isFinite(expiryTs)) {
      hasStale = true;
      continue;
    }
    if (expiryTs > now) {
      active[trimmedId] = expiryTs;
      activeIds.add(trimmedId);
    } else {
      hasStale = true;
    }
  }

  if (hasStale) {
    if (Object.keys(active).length === 0) {
      delete map[normalizedOwnerId];
    } else {
      map[normalizedOwnerId] = active;
    }
    writeSkippedMap(map);
  }

  return activeIds;
}
